import { createContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import { FirstLoginCampaign } from "../util/marketingCampaigns/firstLogin";
import { SendUtmDataToHubSpot } from "../util/marketingCampaigns/firstLogin/strategies";

declare global {
  var _hsq: any[];
}

// From: https://developers.hubspot.com/docs/api/events/tracking-code
const hsq = (window._hsq = window._hsq || []);

const HubSpotContext = createContext(hsq);

/**
 * HubSpotProvider arranges automatic page view tracking and provides
 * the HubSpot queue via context for descendent components to consume
 * as needed.
 *
 * Must be rendered within the Router, and above all Routes.
 */
export default function HubSpotProvider({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  useEffect(() => {
    if (user?.email) {
      hsq.push([
        "identify",
        {
          email: user.email,
        },
      ]);

      if (user?.logins_count === 1) {
        const campaign = new FirstLoginCampaign();
        campaign.setStrategy(new SendUtmDataToHubSpot(user.email));
        campaign.init();
      }
    }
  }, [user?.email, user?.logins_count]);

  useEffect(() => {
    // Gives the page time to render & update the document title,
    // which is included in the page view event by HubSpot automatically.
    const timeout = setTimeout(() => {
      hsq.push(["setPath", `${location.pathname}${location.search || ""}`]);
      hsq.push(["trackPageView"]);
    }, 200);

    return () => clearTimeout(timeout);
  }, [location.pathname, location.search]);

  return (
    <HubSpotContext.Provider value={hsq}>{children}</HubSpotContext.Provider>
  );
}
